<template>
  <b-form ref="projectform">
    <b-form-row>
      <b-col>
        <b-form-group label-class="gk-label-margin gk-modal-label">
          <template slot="label">
            <span v-translate>Customer*</span>
          </template>
          <div v-if="showCustomerSelector">
            <CustomerSelect @selected-customer="customerSelected" searchable />
            <div class="form-required text-danger" v-if="formWarnings.customer">
              <span>{{ $gettext('Select a customer to save this project') }}</span>
            </div>
            <div class="float-right mt-1">
              <GekkoButton 
                @click="showCustomerSelector = !showCustomerSelector"
                :text="$gettext('Create new customer')"
                icon="fa-solid fa-plus"
                class="text-info"
                variant="link"
              />
            </div>
          </div>
          <div v-else>
            <b-input-group>
              <b-form-input id="input-2" type="text" v-model="customer.name" :required="true" :placeholder="$gettext('New Customer name')"></b-form-input>
            </b-input-group>
            <div class="form-required text-danger" v-if="formWarnings.customername">
              <span>{{ $gettext('Customer name is required') }}</span>
            </div>
            <div class="float-right mt-1">
              <GekkoButton 
                @click="showCustomerSelector = !showCustomerSelector"
                :text="$gettext('Back to customer selection')"
                icon="fa-solid fa-caret-left"
                class="text-info"
                variant="link"
              />
            </div>
          </div>

        </b-form-group>
      </b-col>
    </b-form-row>
    <b-form-row>
      <b-col>
        <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" label-for="input-2">
          <template slot="label">
            <span v-translate>New Project name</span>
          </template>
          <b-input-group>
            <b-form-input id="input-2" type="text" v-model="project.name" @input="projectNameInput" :required="false" :placeholder="$gettext('Project_01')"></b-form-input>
          </b-input-group>
          <div class="form-required text-danger" v-if="formWarnings.projectname">
            <span>{{ $gettext('Project name is required') }}</span>
          </div>
        </b-form-group>
        <b-form-group id="input-group-7" label-class="gk-label-margin gk-modal-label" label-for="input-7">
            <template slot="label">
            </template>
            <b-input-group>
                <b-form-checkbox
                    id="input-7"
                    :value="true"
                    :unchecked-value="false"
                    v-model="project.favorite"
                    switch>
                    <span v-translate>Favorite project</span>&nbsp;
                    <font-awesome-icon 
                      id="tt_project_favorite"
                      icon="fa-solid fa-circle-info"
                      class="text-info"
                    />
                    <b-tooltip target="tt_project_favorite" triggers="hover">
                      {{ $gettext('Showed project on top in the hours add page, for fast daily use.') }}
                    </b-tooltip>
                </b-form-checkbox>
            </b-input-group>
        </b-form-group>
      </b-col>
    </b-form-row>
  </b-form>
</template>

<script>
import { mapGetters } from 'vuex';

import ProjectService from "@/store/services/project.service";
import CustomerService from "@/store/services/customer.service";
import GekkoButton from "@/components/GekkoButton";
import CustomerSelect from "@/components/invoices/ui/CustomerSelect"

export default {
  name: "AddEditProjectSimple",
  components: { GekkoButton, CustomerSelect },
  data() {
    return {
      showCustomerSelector: true,
      formWarnings: {
        customer: false,
        customername: false,
        projectname: false
      },
      customer: {},
      project: {}
    }
  },
  computed: {
    ...mapGetters({ 
      getCompanyId: 'main/getCompanyId'
    }),
  },
  watch: {
    showCustomerSelector(newValue) {
      if (!newValue) this.initializeCustomer()
    }
  },
  created() {
    this.initializeCustomer()
    this.initializeProject()
  },
  methods: {
    initializeCustomer () {
      this.customer = {
        name: '',
        contact_name: '',
        email: '',
        phone_number: '',
        kvk: '',
        btw: '',
        is_customer: true,
        is_supplier: false,
        address: '',
        postal_code: '',
        city: '',
        country: '',
        show_country_on_invoice: false,
        description: '',
        hourly_rate: null,
        default_km_rate: null,
        default_payment_term: null,
        owner_company: this.getCompanyId
      }
    },
    initializeProject () {
      this.project = {
        company: null, // id
        name: '',
        contact_name: null,
        reference: '',
        description: '',
        hourly_rate: null,
        favorite: false,
      }
    },
    customerSelected (customer) {
      this.customer.id = customer.customer_id
      this.customer.name = customer.name
      this.formWarnings.customer = false
      this.formWarnings.customername = false
    },
    saveForm() {
      // console.log('saveForm()')
      if(!this.validateForm())
        return false

      if (!this.showCustomerSelector) {
        // create new customer then, attach the new project
        CustomerService.create(this.customer).then((response) => {
            this.$store.commit('setAlert', {
              title: this.$gettext("Contact added"),
              text: this.$gettext("The contact was added to your contact list."),
              variant: "success",
              duration: 4
            });
            if(this.project.name){
              this.createProject(response.data.id)
            }
            else{
              this.$emit('created', response.data);
            }
        }).catch((error) => {
            let msg = this.$gettext('The contact could not be added to your contact list.');
            if (error.data) {
                let firstDictKey = Object.keys(error.data)[0];
                msg = error.data[firstDictKey];
            }
            this.$store.commit('setAlert', {
              title: this.$gettext("Error adding contact"),
              text: msg,
              variant: "danger",
              duration: 4
            });
        });
      } else {
        this.createProject(this.customer.id)
      }
    },
    createProject (customer_id) {
      this.project.company = customer_id
      ProjectService.create(this.project).then((response) => {
        this.$store.commit('setAlert', {
          title: this.$gettext("Project created"),
          text: this.$gettext("Project added to the projects list."),
          variant: "success",
          duration: 4
        });
        this.$emit('created', response.data);
      }).catch((error) => {
        let msg = this.$gettext('The project could not be added to the list.');
        if (error.data) {
          let firstDictKey = Object.keys(error.data)[0];
          msg = error.data[firstDictKey];
        }
        this.$store.commit('setAlert', {
          title: this.$gettext("Error adding project"),
          text: msg,
          variant: "danger",
          duration: 4
        });
      });
    },
    projectNameInput (value) {
      this.formWarnings.projectname = !(value !== '')
    },
    validateForm () {
      let isValid = true
      if(this.showCustomerSelector && !this.customer.id) {
        this.formWarnings.customer = true
        isValid = false
      }
      if(!this.showCustomerSelector && !this.customer.name) {
        this.formWarnings.customername = true
        isValid = false
      }
      return isValid
    }
  }
}
</script>

<style scoped>
.form-element {
  display: inline-block;
  padding: 20px;
}
.action-button {
  display: inline-block;
  padding: 10px;
}
.help-text {
  color: #888;
  font-size: 14px;
}
.form-required {
  font-size: 14px;
  line-height: 1;
  margin-top: 5px;
}
</style>